import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { Config } from '@app/src/config'
import { useAccessibleOrganizations } from '@app/src/context/AccessibleOrganizationsContext'
import { useAccount } from '@app/src/context/AccountContext'
import { Solutions } from '@app/src/wf-constants'
import { useEffect, useMemo, useRef } from 'react'

declare global {
  interface Window {
    Intercom?: (fn: IntercomFunction, prop?: number | string | IntercomProp) => void
  }
}

export enum IntercomSolutionKeys {
  EsgReportingSolution = 'Has ESG Reporting Solution',
  InvestmentsSolution = 'Has Investments Solution',
  SourcingSolution = 'Has Sourcing Solution',
  SourcingStartSolution = 'Has Sourcing Start Solution',
  TransparencySolution = 'Has Transparency Solution',
}

export type IntercomProp = {
  app_id: string
  user_id?: number | string
  name?: string
  email?: string
  created_at?: string | number
  company?: {
    company_id: number
    name: string
  }
  message?: string
  intercom_user_jwt: string
  [IntercomSolutionKeys.EsgReportingSolution]: boolean
  [IntercomSolutionKeys.InvestmentsSolution]: boolean
  [IntercomSolutionKeys.SourcingSolution]: boolean
  [IntercomSolutionKeys.SourcingStartSolution]: boolean
  [IntercomSolutionKeys.TransparencySolution]: boolean
}

export type IntercomFunction = 'boot' | 'showNewMessage' | 'update' | 'shutdown'

const intercomKeyMap = {
  [Solutions.Finance]: IntercomSolutionKeys.EsgReportingSolution,
  [Solutions.Investment]: IntercomSolutionKeys.InvestmentsSolution,
  [Solutions.Sourcing]: IntercomSolutionKeys.SourcingSolution,
  [Solutions.SourcingStart]: IntercomSolutionKeys.SourcingStartSolution,
  [Solutions.Transparency]: IntercomSolutionKeys.TransparencySolution,
}

/**
 * Loads the Intercom widget SDK if not already present.
 *
 * We load it here (in the app bundle) rather than in index.html so that
 * the Intercom settings (INTERCOM_ENABLED, INTERCOM_APP_ID) are read from
 * Config, which merges all sources correctly regardless of deployment target.
 */
function loadIntercomSdk(): void {
  if (!Config.INTERCOM_ENABLED || !Config.INTERCOM_APP_ID) return
  if (typeof window.Intercom === 'function') return

  const appId = Config.INTERCOM_APP_ID

  // Official Intercom installation snippet (adapted to TypeScript).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  const i = (...args: unknown[]) => {
    i.q.push(args)
  }
  i.q = [] as unknown[][]
  i.c = (...args: unknown[]) => {
    i.q.push(args)
  }
  w.Intercom = i

  const s = document.createElement('script')
  s.type = 'text/javascript'
  s.async = true
  s.src = `https://widget.intercom.io/widget/${appId}`
  document.head.appendChild(s)
}

export const sendNewIntercomMessage = (message?: string) => {
  if (window.Intercom) {
    window.Intercom('showNewMessage', message || '')
  }
}

export const logOutOfIntercom = () => {
  if (window.Intercom) window.Intercom('shutdown')
}

const useIntercom = (): void => {
  const sdkLoaded = useRef(false)

  useEffect(() => {
    if (!sdkLoaded.current) {
      loadIntercomSdk()
      sdkLoaded.current = true
    }
  }, [])

  const { account } = useAccount()
  const { accessibleOrganizations, isLoading: isAccessibleOrganizationsLoading } = useAccessibleOrganizations()
  const { familyName, givenName, createdAt, email, id: userId } = account?.user || {}
  const { name: organizationName, id: organizationId } = account?.organization || {}
  const intercomUserId = Config.FRONT_ENV === 'prod' ? userId : `${Config.FRONT_ENV}-${userId}` // prefixing user id by environment (except for prod) to avoid IDs overlap between WF environments using the same Intercom env.
  const name = Boolean(familyName) && Boolean(givenName) ? `${givenName} ${familyName}` : undefined
  const intercomEnabledForEnvironment = Config.INTERCOM_ENABLED
  const {
    data: intercomUserHash,
    isLoading: intercomUserHashLoading,
    isError: intercomUserHashIsError,
  } = useFetchResource<string>({
    endpoint: endpoints.intercomUserHash,
    key: FetchKey.IntercomUserHash,
    shouldShowErrorNotification: false,
    options: { enabled: Boolean(account?.user?.id) && intercomEnabledForEnvironment, retry: 3, retryDelay: 1000 },
  })

  const availableSolutions = useMemo(
    () =>
      accessibleOrganizations.reduce(
        (acc, o) => {
          Object.keys(intercomKeyMap).forEach(key => {
            if (o.solution.some(s => s.solution.toLowerCase() === key)) acc[intercomKeyMap[key]] = true
          })
          return acc
        },
        {
          [IntercomSolutionKeys.EsgReportingSolution]: false,
          [IntercomSolutionKeys.InvestmentsSolution]: false,
          [IntercomSolutionKeys.SourcingSolution]: false,
          [IntercomSolutionKeys.SourcingStartSolution]: false,
          [IntercomSolutionKeys.TransparencySolution]: false,
        },
      ),
    [accessibleOrganizations],
  )

  useEffect(() => {
    if (
      !email ||
      !window.Intercom ||
      isAccessibleOrganizationsLoading ||
      intercomUserHashLoading ||
      !intercomUserHash ||
      intercomUserHashIsError
    )
      return

    window.Intercom('boot', {
      app_id: Config.INTERCOM_APP_ID,
      intercom_user_jwt: intercomUserHash,
      user_id: intercomUserId,
      name,
      email,
      created_at: createdAt ? parseInt((new Date(createdAt).getTime() / 1000).toFixed(0)) : '',
      company: organizationId
        ? {
            company_id: organizationId,
            name: organizationName ?? '',
          }
        : undefined,
      ...availableSolutions,
    })
  }, [
    createdAt,
    familyName,
    givenName,
    email,
    organizationId,
    organizationName,
    userId,
    isAccessibleOrganizationsLoading,
    availableSolutions,
    intercomUserHash,
    intercomUserId,
    intercomUserHashLoading,
    intercomUserHashIsError,
    name,
  ])
}

export default useIntercom
